// An organization's service level credit (D76, D108; APS-474), on its page's side column: for a
// month (the last one that is over, by default), the status page's measured availability against
// each target, D76's credit and what it comes to on the organization's month fee, the day a
// customer claim must come by, and the credit once applied. Says so calmly when the API has no
// STATUS_URL.
import { KV, Section, Status } from '@apsio/ui'
import { useQuery } from '@tanstack/react-query'
import { type ReactNode, useState } from 'react'
import { isApiError } from '../api/client'
import { applySlaCredit, type SlaCredit, slaCreditQuery, useAdminMutation } from '../api/queries'
import { ActionForm } from '../components/ActionForm'
import { MonthChip, NotConfigured } from '../components/kit'
import { ErrorState, Loading } from '../components/states'
import { type MessageId, useI18n } from '../i18n'
import { useFormat } from '../lib/format'

const STATUS: Record<SlaCredit['status'], MessageId> = {
  month_open: 'org.sla.status.month_open',
  no_data: 'org.sla.status.no_data',
  none: 'org.sla.status.none',
  no_subscription: 'org.sla.status.no_subscription',
  ineligible_plan: 'org.sla.status.ineligible_plan',
  no_invoice: 'org.sla.status.no_invoice',
  unpaid: 'org.sla.status.unpaid',
  no_fee: 'org.sla.status.no_fee',
  credited: 'org.sla.status.credited',
  due: 'org.sla.status.due',
}

/** Each state's tone and glyph (Mosaic): a credit to act on, one applied or met, or nothing. */
const TONE: Record<SlaCredit['status'], ['ok' | 'miss' | 'mute', string]> = {
  month_open: ['mute', '○'],
  no_data: ['mute', '–'],
  none: ['ok', '✓'],
  no_subscription: ['mute', '–'],
  ineligible_plan: ['mute', '–'],
  no_invoice: ['miss', '!'],
  unpaid: ['mute', '○'],
  no_fee: ['miss', '!'],
  credited: ['ok', '✓'],
  due: ['miss', '!'],
}
/** The dim line under a state that needs one. */
const WHY: Partial<Record<SlaCredit['status'], MessageId>> = {
  no_data: 'org.sla.why.no_data',
  none: 'org.sla.why.none',
  no_invoice: 'org.sla.why.no_invoice',
  unpaid: 'org.sla.why.unpaid',
  no_fee: 'org.sla.why.no_fee',
}

/** The last calendar month that is over (UTC), `YYYY-MM`. */
export function lastMonth(now = new Date()): string {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1))
    .toISOString()
    .slice(0, 7)
}

export function OrgSla({ orgId }: { orgId: string }) {
  const { t } = useI18n()
  const [month, setMonth] = useState(lastMonth)
  const q = useQuery(slaCreditQuery(orgId, month))
  return (
    <Section title={t('org.sla.title')} right={<MonthChip value={month} onChange={setMonth} />}>
      {q.isPending ? (
        <Loading />
      ) : q.isError ? (
        isApiError(q.error) && q.error.code === 'not_configured' ? (
          <NotConfigured missing={t('org.sla.notConfigured')} task={t('kit.ownerTask')} />
        ) : (
          <ErrorState error={q.error} retry={() => q.refetch()} />
        )
      ) : (
        <SlaBody orgId={orgId} sla={q.data} />
      )}
    </Section>
  )
}

function SlaBody({ orgId, sla }: { orgId: string; sla: SlaCredit }) {
  const { t, number, date } = useI18n()
  const { money } = useFormat()
  const pct = (value: number, digits: number) =>
    `${number(value, { maximumFractionDigits: digits })} %`
  // A service under its target reads as missed (Mosaic): the crash tone and its glyph.
  const uptime = (value: number | null, target: number): ReactNode => {
    if (value === null) return t('org.sla.noData')
    const text = t('org.sla.uptime', { uptime: pct(value, 3), target: pct(target, 1) })
    return value < target ? <span className="sla-miss">× {text}</span> : text
  }
  const day = (ymd: string) =>
    date(`${ymd}T00:00:00Z`, { day: 'numeric', month: 'short', timeZone: 'UTC' })
  const items: [string, ReactNode][] = [
    [t('org.sla.ingest'), uptime(sla.uptime.ingest, sla.targets.ingest)],
    [t('org.sla.console'), uptime(sla.uptime.console, sla.targets.console)],
  ]
  if (sla.percent > 0)
    items.push([t('org.sla.credit'), t('org.sla.creditValue', { percent: pct(sla.percent, 0) })])
  if (sla.fee_cents !== null && sla.currency) {
    const fee = money(sla.fee_cents, sla.currency)
    items.push([
      t('org.sla.fee'),
      sla.interval === 'year' ? (
        <>
          {fee}
          <span className="sla-sub">{t('org.sla.feeYear')}</span>
        </>
      ) : (
        fee
      ),
    ])
  }
  if (sla.status === 'due' && sla.currency) {
    items.push([t('org.sla.due'), money(sla.credit_cents, sla.currency)])
    items.push([
      t('org.sla.claimBy'),
      sla.claim_open ? day(sla.claim_by) : t('org.sla.claimClosed', { date: day(sla.claim_by) }),
    ])
  }
  if (sla.credited)
    items.push([
      t('org.sla.credited'),
      t('org.sla.creditedValue', {
        amount: money(sla.credited.amount_cents, sla.credited.currency),
        date: day(sla.credited.at.slice(0, 10)),
      }),
    ])
  return (
    <>
      <p className="sla-state">
        <Status s={TONE[sla.status][0]} glyph={TONE[sla.status][1]} label={t(STATUS[sla.status])} />
        {sla.status === 'unpaid' && sla.invoice === 'draft' ? (
          // A renewal Stripe has not finalized yet (about an hour): not late, just early (APS-505).
          <span className="sla-why">{t('org.sla.why.draft')}</span>
        ) : WHY[sla.status] ? (
          <span className="sla-why">{t(WHY[sla.status] as MessageId)}</span>
        ) : null}
      </p>
      <KV items={items} />
      {sla.status === 'due' && sla.currency ? (
        <div className="sla-apply">
          <ApplyCredit
            orgId={orgId}
            month={sla.month}
            amount={money(sla.credit_cents, sla.currency)}
          />
        </div>
      ) : null}
    </>
  )
}

/**
 * Staff apply the month's credit with a reason (a recent sign-in, both audit logs); the API works
 * out the amount.
 */
function ApplyCredit({ orgId, month, amount }: { orgId: string; month: string; amount: string }) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const apply = useAdminMutation(applySlaCredit, [
    ['sla', orgId],
    ['org', orgId],
  ])
  // An inline ghost button under the list (admin-d: never a full-width button), then its form.
  return open ? (
    <ActionForm
      open
      onClose={() => setOpen(false)}
      label={t('org.sla.apply')}
      confirm={t('org.sla.applyConfirm', { amount })}
      reasonHint={t('org.sla.applyHint')}
      onSubmit={async (reason) => {
        await apply.mutateAsync({ orgId, month, reason })
      }}
    />
  ) : (
    <button type="button" className="btn ghost sm" onClick={() => setOpen(true)}>
      {t('org.sla.apply')}
    </button>
  )
}
